import { Component, effect, EventEmitter, inject, Output, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardComponent } from '@core/components/ui-card/card.component';
import { CustomSpinnerComponent } from '@core/components/ui-custom-spinner/custom-spinner.component';
import type { MediaButton } from '@core/models/media-button.interface';
import type { Banner } from '@core/components/banner/banner.interfaces';
import { BannerComponent } from '@core/components/banner/banner.component';
import type { InstructionStepper } from '@core/components/instruction-stepper/instruction-stepper.interfaces';
import {
  LinearStepFontSize,
  LinearStepInfo,
  LinearStepStatusColor,
} from '@core/components/linear-step-info/linear-step-info.interfaces';
import { TimerPipe } from '@core/pipes/timer.pipe';
import { VoucherHeaderComponent } from '@core/components/voucher-header/voucher-header.component';
import type { DirectConnectionsVoucher } from '@core/components/direct-connections-voucher/direct-connections-voucher.interface';
import { DirectConnectionsVoucherComponent } from '@core/components/direct-connections-voucher/direct-connections-voucher.component';
import { VoucherPdfContainerComponent } from '@core/components/voucher-pdf-container/voucher-pdf-container.component';

import { PdfJsService } from '@core/services/pdf-js.service';
import { CaptureScreenService } from '@core/services/capture-screen.service';
import { CashProcessorService } from '@core/services/cash-processor.service';
import type {
  GetPaymentPayloadRequest,
  GetPaymentResponseRequest,
  PaymentMethodInstruction,
  PaymentMethodPersonaType,
} from '@core/services/cash-processor.interface';
import { ProcessorNetworkseComponent } from '@core/components/processor-networks/processor-networks.component';
import { ProcessorSelectableComponent } from '@core/components/processor-selectable/processor-selectable.component';
import type { Processors } from '@core/components/processor-selectable/processor-selectable.interfaces';
import {
  bankTransferPaymentMethodButton,
  cashPaymentMethodButton,
  walletPaymentMethodButton,
} from '@core/components/direct-connections-voucher-container/direct-connections-voucher.utils';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { EnvironmentService } from '@core/services/environment.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'checkout-clinng-voucher-container',
  standalone: true,
  imports: [
    CommonModule,
    CustomSpinnerComponent,
    CardComponent,
    BannerComponent,
    VoucherHeaderComponent,
    DirectConnectionsVoucherComponent,
    VoucherPdfContainerComponent,
    ProcessorSelectableComponent,
    ProcessorNetworkseComponent,
  ],
  providers: [TimerPipe],
  templateUrl: './checkout-clinng-voucher-container.component.html',
  styleUrl: './checkout-clinng-voucher-container.component.scss',
})
export class CheckoutClinngVoucherContainerComponent implements OnDestroy {
  @Output() onShowTemplate: EventEmitter<boolean> = new EventEmitter<boolean>(false);

  jsPdfService = inject(PdfJsService)
  screenRecordService = inject(CaptureScreenService)

  showTemplate: boolean = false

  isLoading = false;

  expirationTimeFormated: string = '';

  directConnectionsVoucherInput: DirectConnectionsVoucher = {} as DirectConnectionsVoucher;
  paymentParemeters: GetPaymentPayloadRequest = {} as GetPaymentPayloadRequest;

  voucherData: GetPaymentResponseRequest = null as unknown as any;
  selectedProcessor: Processors = null as any

  linearSteps: LinearStepInfo[] = []
  processors: Processors[] = []

  paymentMethod: string = 'Cash'

  private timerPipe = new TimerPipe();
  private timerSubscription?: Subscription;

  get bankTransferButton(): MediaButton {
    return bankTransferPaymentMethodButton(this.paymentMethod, () => {
      this.paymentMethod = 'BankTransfer'
      this.changeProcessorInstructions()
    })

  }

  get banner(): Banner {
    return {
      processor: this.selectedProcessor.name,
      text: 'Estás Pagando con',
      img: this.selectedProcessor.img,
      background: this.selectedProcessor.hexColor || '',
      colorText: '#2B2F3A'
    };
  }
  get cashButton(): MediaButton {
    return cashPaymentMethodButton(this.paymentMethod, () => {
      this.paymentMethod = 'Cash'
      this.changeProcessorInstructions()
    })
  }

  get walletButton(): MediaButton {
    return walletPaymentMethodButton(this.paymentMethod, () => {
      this.paymentMethod = 'Wallet'
      this.changeProcessorInstructions()
    })
  }



  get instructions(): PaymentMethodInstruction[] {
    let instructions = this.voucherData?.voucherConfig?.bankList.find((bankInstruction) => bankInstruction.bankCode === this.selectedProcessor?.name)?.paymentMethodInstructions

    return instructions || []
  }

  constructor(
    private cashProcessorService: CashProcessorService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private environmentService: EnvironmentService
  ) {
    effect(() => {

      this.isLoading = this.cashProcessorService.paymentLoading()

      if (this.cashProcessorService.paymentData().voucherConfig) {
        this.voucherData = { ...this.cashProcessorService.paymentData(), voucherConfig: JSON.parse(this.cashProcessorService.paymentData()?.voucherConfig as unknown as any) }
        this.paymentMethod = this.voucherData.voucherConfig.methodSelected || this.voucherData.voucherConfig.instruction_payment_methods[0]
        this.initTimer()

      }

      if (this.cashProcessorService.getPaymentError() || this.cashProcessorService.paymentData().errorMsg) {
        this.router.navigate(['/error'])
      }
      this.refreshVoucher()

    })

    effect(() => {
      if (this.screenRecordService.generatedImage()) {
        (() => {
          this.downloadImage(this.screenRecordService.generatedImage())
          this.onShowTemplateFunc(false)
          window.location.reload()
        })()
      }
    })
  }

  ngOnInit() {
    this.paymentParemeters = {
      operationId: this.activatedRoute.snapshot.params['operationId'],
      merchantId: this.activatedRoute.snapshot.params['merchantId'],
      verification: this.activatedRoute.snapshot.queryParams['verification'],
    }

    this.getPaymentInfo()
    this.setGetVoucherDataInterval()
  }

  ngOnDestroy() {
    this.timerSubscription?.unsubscribe();
  }

  refreshVoucher(): void {
    this.initLinearSteps()
    this.initInstructionSteps()
    this.initProcessors()
    this.changeProcessorInstructions()
  }

  metodoPagoTipoPersona!: PaymentMethodPersonaType;
  handleCambioMetodo(metodo: PaymentMethodPersonaType) {
    this.metodoPagoTipoPersona = metodo;
  }

  initProcessors(): void {
    const environment = this.environmentService?.env?.production ? 'prod' : 'dev';
    this.processors = this.voucherData?.voucherConfig?.bankList.map((bank) => {
      return {
        name: bank?.bankCode || '',
        img: `https://d3os9iahmyv2xb.cloudfront.net/${environment}/voucher/V1/collectors/${bank.bankCode}/banner.svg`,
        hexColor: bank?.hexColor || '',
      } as unknown as Processors
    }) || []
    this.selectedProcessor = this.processors[0]
  }

  initLinearSteps(): void {
    const steps = []

    const amount: LinearStepInfo = {
      label: {
        label: 'Monto a pagar',
        value: `<b>${this.voucherData?.currencyISO} ${this.voucherData?.amount}</b>`,
        fontSize: LinearStepFontSize.SMALL,
      },
    }

    const paymentCode: LinearStepInfo = {
      label: {
        label: 'Código de pago',
        value: `<b>${this.voucherData?.paymentCode}</b>`,
        color: LinearStepStatusColor.PURPLE,
        fontSize: LinearStepFontSize.LARGE,
      },
      clipboard: true,
      clipboardText: `${this.voucherData?.paymentCode}`,
    }

    if (window.innerWidth <= 600) {
      steps.push(paymentCode)
      steps.push(amount)
    }

    if (window.innerWidth > 600) {
      steps.push(amount)
      steps.push(paymentCode)
    }

    if (this.voucherData?.status === 'EXPIRADO') {
      steps.push({
        label: {
          label: `Estado`,
          value: 'Tiempo expirado',
          color: LinearStepStatusColor.RED,
          fontSize: LinearStepFontSize.SMALL,
        },
      })
    }

    if (this.voucherData?.status === 'AUTORIZADO') {
      steps.push({
        label: {
          label: `Estado`,
          value: 'Pago Realizado',
          color: LinearStepStatusColor.GREEN,
          fontSize: LinearStepFontSize.SMALL,
        },
      })
    }

    if (this.voucherData?.status === 'PENDIENTE_PAGO') {
      steps.push({
        label: {
          label: `Vence en`,
          value: `<b>${this.expirationTimeFormated}</b> horas`,
          fontSize: LinearStepFontSize.SMALL,
        },
      })
    }

    this.linearSteps = steps
  }

  initInstructionSteps(): void {
    let directConnectionsVoucherData = {
      paymentMethodButtons: [],
      downloadButton: {
        imagesUrl: ['./assets/svg/download-file.svg'],
        label: 'Descargar instrucciones',
        color: '#6A00F4',
        onClick: () => {
          this.generatePdf()
        },
      },
      instructions: []
    }

    this.directConnectionsVoucherInput = directConnectionsVoucherData as unknown as DirectConnectionsVoucher
  }

  changeProcessorInstructions(): void {
    let instruction: InstructionStepper[] = this.findInstruction()
    let buttons: MediaButton[] = []

    this.uploadButton([])

    let cashMethod = this.findCashMethod()
    let bankTransferMethod = this.findBankTransferMethod()
    let walletMethod = this.findWalletMethod()

    this.updateVoucherInstructions(instruction)

    if (bankTransferMethod) {
      buttons.push(this.bankTransferButton)
    }

    if (cashMethod) {
      buttons.push(this.cashButton)
    }

    if (walletMethod) {
      buttons.push(this.walletButton)
    }

    this.uploadButton(buttons)
  }

  uploadButton(media: MediaButton[]): void {
    this.directConnectionsVoucherInput = {
      ...this.directConnectionsVoucherInput,
      paymentMethodButtons: media,
    }
  }

  updateVoucherInstructions(instructions: InstructionStepper[]): void {
    this.directConnectionsVoucherInput = {
      ...this.directConnectionsVoucherInput,
      instructions: instructions,
    }
  }

  findInstruction(): InstructionStepper[] {
    if (!this.voucherData) {
      return []
    }

    const collectorsByMethod = this.voucherData.voucherConfig?.bankList?.filter(b => {
      if(b.paymentMethodInstructions) {
        return b.paymentMethodInstructions?.some(instruction => instruction.method === this.paymentMethod)
      }
      return false
    });
    const processor = collectorsByMethod?.find(b => b.bankCode === this.selectedProcessor.name);
    if (processor) {
      const method = processor?.paymentMethodInstructions.find(m => m.method === this.paymentMethod);
      if (method) {
        const instructions = method?.instructions.map(i => {
          return {
            step: i.order,
            description: i.value,
          } as InstructionStepper
        })

        return instructions
      }
    }

    return []
  }

  findCashMethod(): PaymentMethodInstruction {
    return this.findPaymentMethodInstruction('Cash') || null as any
  }

  findBankTransferMethod(): PaymentMethodInstruction {
    return this.findPaymentMethodInstruction('BankTransfer') || null as any
  }

  findWalletMethod(): PaymentMethodInstruction {
    return this.findPaymentMethodInstruction('Wallet') || null as any
  }

  findPaymentMethodInstruction(method: string): PaymentMethodInstruction | undefined {
    return this.instructions?.find((instruction) => instruction.method === method)
  }

  findProcessor(code: string): Processors {
    return this.processors.find((p) => p.name === code) as Processors;
  }

  initTimer(): void {
    this.timerSubscription?.unsubscribe();

    const expirationDate = moment(this.voucherData?.expiration).utc(true).toDate();

    this.timerSubscription = this.timerPipe.transform(expirationDate).subscribe((time) => {
        this.expirationTimeFormated = time;
        if (this.expirationTimeFormated === '00:00:00' && this.voucherData.status != 'AUTORIZADO') {
          this.voucherData.status = 'EXPIRADO';
        }

        this.initLinearSteps();
      });
  }

  getPaymentInfo(): void {
    this.cashProcessorService.getPayment(this.paymentParemeters)
  }

  handleChangeMethod(method: string) {
    this.paymentMethod = method
  }

  onProcessorChange(processor: Processors): void {
    this.selectedProcessor = processor;
    this.changeProcessorInstructions()
  }

  generatePdf(): void {
    this.onShowTemplateFunc(true)
    this.onSetDownloadButtonToLoading(true)

    setTimeout(() => {
      this.screenRecordService.captureScreen('voucherTemplate')
    }, 200);
  }

  onShowTemplateFunc(data: boolean): void {
    this.showTemplate = data
    this.onShowTemplate.emit(this.showTemplate)
  }

  generatePdfByImage(image: string): void {
    const pdfWidth = 210
    let voucherHeight = 300

    if (this.voucherData.voucherConfig.bankList.length === 1) {
      voucherHeight = 200
    }

    if (this.voucherData.voucherConfig.bankList.length === 2) {
      voucherHeight = 250
    }

    this.jsPdfService.addImage(
      image,
      'JPEG',
      0, //X posición en el PDF
      0, //Y posición en el PDF
      pdfWidth, //Ancho en el PDF
      voucherHeight
    );

    this.jsPdfService.save('Instrucciones de pago')
  }

  downloadImage(image: string): void {
    var a = document.createElement("a")
    a.href = image
    a.download = "Instrucciones de pago"
    a.click()
    this.onSetDownloadButtonToLoading(false)
  }

  onSetDownloadButtonToLoading(loading: boolean): void {
    this.directConnectionsVoucherInput = {
      ...this.directConnectionsVoucherInput,
      downloadButton: {
        ...this.directConnectionsVoucherInput.downloadButton,
        isLoading: loading,
      },
    }
  }

  setGetVoucherDataInterval(): void {
    setInterval(() => {
      this.getPaymentInfo()
    }, 60000)
  }
}
